import { useQuery } from '@tanstack/react-query';
import { FileBarChart2, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EmptyState } from '@/components/shared/states';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { PlanUpgradePrompt } from '@/components/shared/plan-upgrade-prompt';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, isAdminOrOwner, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { useAuthStore } from '@/stores/auth-store';
import { useChurchStore } from '@/stores/church-store';

import { ReportsSubnav } from './reports-subnav';
import { ReportFavoriteButton } from './report-favorite-button';

interface CustomReportRow {
  id: string;
  name: string;
  description: string | null;
  objectSource: string;
  createdBy: string | null;
}

export const CustomReportsListPage = () => {
  const { t } = useTranslation('reports');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const membership = useCurrentMembership();
  const allowsFeature = useChurchStore((state) => state.allowsFeature);
  const userId = useAuthStore((state) => state.user?.id);
  const churchId = useChurchStore((state) => state.church?.id);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const canCreate =
    allowsFeature('custom_reports') && hasPermission(membership, PERMISSIONS.REPORTS_CREATE);

  const list = useQuery({
    queryKey: ['reports', 'custom', page, search],
    enabled: allowsFeature('custom_reports'),
    queryFn: () =>
      api.get<{ data: CustomReportRow[]; page: { totalItems: number; totalPages: number } }>(
        '/reports/custom',
        { query: { page, pageSize: 25, search: search.trim() || undefined } },
      ),
  });

  const canManageReport = (report: CustomReportRow) =>
    isAdminOrOwner(membership) || report.createdBy === userId;

  const columns: DataTableColumn<CustomReportRow>[] = useMemo(
    () => [
      {
        id: 'name',
        header: t('custom.columns.name'),
        cell: (row) => (
          <div className="flex items-center gap-1">
            <span className="font-medium text-brand">{row.name}</span>
            <ReportFavoriteButton
              favoriteRef={{ kind: 'custom', id: row.id }}
              churchId={churchId}
              userId={userId}
            />
          </div>
        ),
      },
      {
        id: 'objectSource',
        header: t('custom.columns.source'),
        cell: (row) => t(`custom.sources.${row.objectSource}`, row.objectSource),
      },
      {
        id: 'description',
        header: t('custom.columns.description'),
        cell: (row) => row.description ?? '—',
        hideBelowMd: true,
      },
    ],
    [churchId, t, userId],
  );

  const rows = list.data?.data ?? [];
  const hasSearch = search.trim().length > 0;

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/reports/custom/${deleteId}`);
      toast.success(t('custom.deleted'));
      setDeleteId(null);
      void list.refetch();
    } catch (error) {
      if (error instanceof ApiError) toast.error(tErrors(error.code, error.params));
      else toast.error(tCommon('toast.somethingWentWrong'));
    } finally {
      setDeleting(false);
    }
  };

  if (!allowsFeature('custom_reports')) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('title')} description={t('description')} />
        <ReportsSubnav />
        <PlanUpgradePrompt
          title={t('custom.upgradeTitle')}
          description={t('custom.upgradeBody')}
          planNote={t('custom.upgradePlan')}
          variant="full"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('title')}
        description={t('description')}
        headerActions={
          canCreate ? (
            <Button onClick={() => navigate('/reports/custom/new')}>
              <Plus className="size-4" aria-hidden />
              {t('custom.newReport')}
            </Button>
          ) : undefined
        }
      />
      <ReportsSubnav />

      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-fg-tertiary" />
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder={tCommon('search.placeholder')}
          className="pl-9"
        />
      </div>

      {!list.isLoading && rows.length === 0 && !hasSearch ? (
        <EmptyState
          icon={<FileBarChart2 className="size-6" />}
          title={t('custom.emptyTitle')}
          body={t('custom.emptyBody')}
          action={
            canCreate ? (
              <Button onClick={() => navigate('/reports/custom/new')}>{t('custom.newReport')}</Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(row) => row.id}
            loading={list.isLoading}
            empty={
              <p className="text-body-sm text-fg-secondary">
                {hasSearch ? tCommon('state.noFilterResults') : t('custom.emptyTitle')}
              </p>
            }
            onRowClick={(row) => navigate(`/reports/custom/${row.id}`)}
            rowActions={(row) => (
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate(`/reports/custom/${row.id}`)}
                >
                  {t('custom.run')}
                </Button>
                {canCreate && canManageReport(row) ? (
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => navigate(`/reports/custom/${row.id}/edit`)}
                    >
                      {tCommon('action.edit')}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setDeleteId(row.id)}>
                      {t('custom.delete')}
                    </Button>
                  </>
                ) : null}
              </div>
            )}
            actionsHeader={tCommon('table.actions')}
          />

          {list.data?.page ? (
            <Pagination
              page={page}
              pageSize={25}
              totalItems={list.data.page.totalItems}
              totalPages={list.data.page.totalPages}
              onPageChange={setPage}
              labels={{
                showing: ({ from, to, total }) =>
                  tCommon('pagination.showing', { from, to, total }),
                perPage: tCommon('pagination.perPage'),
                first: tCommon('pagination.first'),
                previous: tCommon('pagination.previous'),
                next: tCommon('pagination.next'),
                last: tCommon('pagination.last'),
              }}
            />
          ) : null}
        </>
      )}

      <ConfirmDialog
        open={deleteId !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteId(null);
        }}
        title={t('custom.deleteTitle')}
        description={t('custom.deleteBody')}
        confirmLabel={t('custom.delete')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={deleting}
        onConfirm={handleDelete}
      />
    </div>
  );
};
